import { Body, Icon, I, Row } from '@leanin/ui';
import { Children, type ReactNode } from 'react';

export type InlineStatusTone = 'destructive' | 'warning' | 'success';

/**
 * Inline status / error / success message with the canonical aria-live
 * region. Replaces the repeated `<span role="alert" aria-live="polite">
 * <Body color="destructive">…</Body></span>` pattern.
 *
 * The glyph is non-color cue — required for a11y baseline so the state
 * cue isn't conveyed by color alone. Each tone has a sane default glyph;
 * override via the `icon` prop when the surrounding context calls for it.
 *
 * `tone="destructive"` defaults to `assertive` aria-live so SR users hear
 * the error immediately; success/warning stay `polite`.
 *
 * A live region announces a change to its text; one inserted already holding
 * the text is not reliably read. Where a message appears in response to an
 * action, keep the status mounted for the surface's whole life and pass
 * nothing (`null`, `undefined`, `false`, `''`) while there is nothing to say:
 * the region renders empty, glyph included, and the message is announced
 * when it arrives. Keep `tone` within one politeness
 * (destructive, or success/warning) for a given mount, so the region's role
 * stays put.
 */
export function InlineStatus({
  children,
  tone = 'destructive',
  size = 'sm',
  icon,
}: {
  children: ReactNode;
  tone?: InlineStatusTone;
  size?: 'sm' | 'md';
  icon?: ReactNode;
}) {
  const defaultGlyph = tone === 'success' ? I.check : I.alert;
  const color =
    tone === 'destructive'
      ? 'var(--destructive)'
      : tone === 'success'
        ? 'var(--success)'
        : 'var(--warning-ink)';
  const live = tone === 'destructive' ? 'assertive' : 'polite';
  return (
    <span role={tone === 'destructive' ? 'alert' : 'status'} aria-live={live}>
      {isEmpty(children) ? null : (
        <Row gap="xs" align="center">
          <Icon size="sm" color={color}>{icon ?? defaultGlyph}</Icon>
          <Body size={size} color={tone} as="span">{children}</Body>
        </Row>
      )}
    </span>
  );
}

// What React renders as nothing (null, undefined, booleans), or as no text.
function isEmpty(children: ReactNode): boolean {
  return Children.toArray(children).every((child) => child === '');
}
